import { useCallback, useMemo } from "react";
import React from "react";

import { Rename } from "components/Prompts";
import { Create } from "components/Prompts/Create";
import { useActionHook, useDealParamsHooks, useModals, useNavigate } from "hooks";
import { useDealRoleGuard, usePrompt, useRoleGuardHook } from "hooks";
import {
  IApiServiceError,
  useDealActions,
  useDealCSSelector,
  useDealSelector,
  useDocumentsActions,
  useFolderSelect,
  useTaskManageActions,
  useUserIdSelector,
  useViewActions
} from "store";
import { FolderKind } from "store/types";

import { EntityType, RoleName } from "../../../constants";

interface UseFolderHookParams {
  folder: {
    id: number;
    title: string;
    is_private: boolean;
    parent_id: number | null;
    user_id: number;
    kind: FolderKind;
    deal_id: number | null;
  };
  refetchHandler?: () => Promise<void>;
}

export const useFolderHook = ({ folder, refetchHandler }: UseFolderHookParams) => {
  const { updateFolderAction, deleteFolderAction, createFolderAction, createFormAction, patchFolderMetaDetailAction } =
    useDealActions();
  const userId = useUserIdSelector();
  const { dealId: rawDealId } = useDealParamsHooks();
  const dealId = Number.isNaN(rawDealId) ? null : rawDealId;
  const dealFromStore = useDealSelector();
  const parentFolder = useFolderSelect(folder?.parent_id ?? 0);
  const navigate = useNavigate();

  const { setShareDialog } = useViewActions();
  const { deleteModal, closeDeleteModal, errorModal, createModal, selectDealFolderModal, filesUploadModal } =
    useModals();
  const { createDocumentAction } = useDocumentsActions();
  const { setCreateAction } = useTaskManageActions();
  const openPrompt = usePrompt();
  const capitalSeekers = useDealCSSelector();
  const capitalSeeker = capitalSeekers?.[0];

  const isRoot = folder.id === -1;
  const effectiveDealId = isRoot ? dealId : folder.deal_id;

  const { action: updateFolder } = useActionHook(updateFolderAction);

  const onCreateNewFolder: (() => void) | undefined = useMemo(
    () => () =>
      createModal({
        action: async (name: string) => {
          const newFolder = await createFolderAction({
            dealId: effectiveDealId!,
            title: name,
            parent_id: isRoot ? null : folder!.id
          });
          await refetchHandler?.();
          navigate({
            route: "dealFolder",
            params: { deal_id: effectiveDealId!, folder_id: newFolder.id },
            search: { section: newFolder.id }
          });
        },
        title: "Add Folder",
        submitButtonTitle: "Add"
      }),
    [isRoot, effectiveDealId, folder, createModal, createFolderAction, refetchHandler, navigate]
  );

  const onFileUpload = useCallback(async () => {
    filesUploadModal({
      dealId: dealId ?? undefined,
      folderId: folder?.id === -1 ? undefined : folder?.id,
      onSuccessCallback: () => refetchHandler?.() ?? Promise.resolve()
    });
  }, [dealId, folder?.id, refetchHandler]);

  const onDropFiles = useCallback(
    (files: File[]) => {
      filesUploadModal({
        dealId: dealId ?? undefined,
        folderId: folder?.id,
        files,
        onSuccessCallback: () => refetchHandler?.() ?? Promise.resolve()
      });
    },
    [dealId, folder?.id, refetchHandler]
  );

  const onRenameFolder = useCallback(
    async (menuTriggerRef: React.RefObject<HTMLButtonElement>, onMenuClose: () => void) => {
      if (isRoot || !folder) return;
      const trigger = menuTriggerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const anchorPosition = {
        top: Math.round(triggerRect.bottom),
        left: Math.round(triggerRect.left)
      };

      onMenuClose();

      const title = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorPosition,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        props: { title: folder.title }
      });
      if (title) {
        await updateFolder({ folderId: folder.id, dealId: effectiveDealId!, title });
        patchFolderMetaDetailAction({
          key: "patch",
          id: folder.id,
          entityType: EntityType.SECTION,
          patch: { title }
        });
      }
    },
    [isRoot, folder, openPrompt, updateFolder, patchFolderMetaDetailAction, effectiveDealId]
  );

  const getPostDeleteNavigation = useCallback(() => {
    const isRootFolder = !folder.parent_id;
    const dealIdForNav = effectiveDealId ?? folder.deal_id ?? null;

    if (isRootFolder) {
      return { route: "dealWorkspace" as const, params: { deal_id: dealIdForNav! } };
    }

    const parentId = folder.parent_id!;
    const search = { section: parentId };
    return {
      route: "dealFolder" as const,
      params: { deal_id: dealIdForNav!, folder_id: parentId },
      search
    };
  }, [folder.parent_id, folder.deal_id, effectiveDealId]);

  const handleDelete = async (resolve?: Function) => {
    if (isRoot || !folder) return;
    try {
      const nav = getPostDeleteNavigation();
      await deleteFolderAction({ dealId: effectiveDealId ?? 0, folderId: folder.id });
      resolve?.(true);
      closeDeleteModal();
      navigate({ ...nav });

      patchFolderMetaDetailAction({
        key: "delete",
        id: folder.id,
        entityType: EntityType.SECTION
      });
    } catch (e) {
      errorModal({ msg: (e as IApiServiceError).message });
    }
  };

  const onDeleteFolder: (() => void) | undefined = useRoleGuardHook(() => {
    if (isRoot || !folder) return undefined;
    return deleteModal({
      element: "Delete folder permanently?",
      message: "This cannot be undone. All contents will be deleted.",
      onPressSuccess: "Delete permanently",
      action: async () => handleDelete()
    });
  }, [RoleName.Analyst, RoleName.Admin]);

  const onShareDialog: (() => void) | undefined = useRoleGuardHook(() => {
    if (isRoot || !folder) return undefined;
    setShareDialog({
      elements: [folder.id],
      type: "section_id",
      shareName: folder.title,
      dealId: folder.deal_id,
      dealCapitalSeekerEmail: capitalSeeker?.email,
      itemToShare: folder.is_private ? null : folder.id,
      cb: () => {
        refetchHandler?.();
      }
    });
  }, [RoleName.Admin, RoleName.Analyst]);

  const checkOnSetPrivatePermission = () => {
    if (isRoot || !folder) return false;
    if (folder.user_id === userId && !folder.parent_id) {
      return true;
    }
    if (folder.user_id === userId && folder.parent_id && !parentFolder?.is_private) {
      return true;
    }
    return false;
  };

  const onPrivate = useCallback(async () => {
    if (isRoot || !folder) return;
    try {
      await updateFolder({
        folderId: folder.id,
        dealId: effectiveDealId!,
        is_private: !folder.is_private
      });
      patchFolderMetaDetailAction({
        key: "patch",
        id: folder.id,
        entityType: EntityType.SECTION,
        patch: { is_private: !folder.is_private }
      });
    } catch (error) {
      errorModal({ msg: (error as IApiServiceError).message });
    }
  }, [folder, isRoot, effectiveDealId, updateFolder, patchFolderMetaDetailAction, errorModal]);

  const onSetPrivate: (() => void) | undefined = useRoleGuardHook(
    checkOnSetPrivatePermission() ? onPrivate : undefined,
    [RoleName.Admin, RoleName.Analyst]
  );

  const onAddForm = useCallback(async () => {
    let selectedSectionId: string | number | undefined;
    if (isRoot || !folder || folder.kind !== "folder") {
      selectedSectionId = await new Promise<string | number | undefined>((resolve) => {
        selectDealFolderModal({
          isOpen: true,
          title: "Add a new form",
          preselectedDeal: { id: effectiveDealId!, title: dealFromStore?.title ?? "" },
          onSubmit: ({ selectedFolder }) => {
            resolve(selectedFolder?.id);
          },
          onClose: () => {
            resolve(undefined);
          }
        });
      });
    } else {
      selectedSectionId = folder.id;
    }
    if (!selectedSectionId) return;

    const newFormTitle = await openPrompt({
      Component: Create,
      props: {
        title: "Add a new form"
      }
    });
    if (!newFormTitle) return;

    const createdForm = await createFormAction({
      dealId: effectiveDealId!,
      sectionId: Number(selectedSectionId),
      title: newFormTitle
    });
    await refetchHandler?.();
    navigate({
      route: "dealForm",
      params: { deal_id: effectiveDealId!, form_id: createdForm.id }
    });
  }, [
    isRoot,
    folder,
    effectiveDealId,
    dealFromStore?.title,
    selectDealFolderModal,
    navigate,
    openPrompt,
    createFormAction,
    refetchHandler
  ]);

  const onAddDocument = useCallback(async () => {
    let selectedSectionId: string | number | undefined;
    if (isRoot || !folder || folder.kind !== "folder") {
      selectedSectionId = await new Promise<string | number | undefined>((resolve) => {
        selectDealFolderModal({
          isOpen: true,
          title: "Add a new document",
          preselectedDeal: { id: effectiveDealId!, title: dealFromStore?.title ?? "" },
          onSubmit: ({ selectedFolder }) => {
            resolve(selectedFolder?.id);
          },
          onClose: () => {
            resolve(undefined);
          }
        });
      });
    } else {
      selectedSectionId = folder.id;
    }
    if (!selectedSectionId) return;

    const newDocumentTitle = await openPrompt({
      Component: Create,
      props: {
        title: "Add a new document"
      }
    });
    if (!newDocumentTitle) return;

    const createdDocument = await createDocumentAction({
      deal_id: effectiveDealId!,
      section_id: Number(selectedSectionId),
      title: newDocumentTitle
    });
    await refetchHandler?.();

    navigate({
      route: "dealDocument",
      params: { deal_id: effectiveDealId!, document_id: createdDocument.id }
    });
  }, [
    isRoot,
    folder,
    effectiveDealId,
    dealFromStore?.title,
    selectDealFolderModal,
    navigate,
    openPrompt,
    createDocumentAction,
    refetchHandler
  ]);

  const onAddTask = useCallback(() => {
    setCreateAction({
      isOpen: true,
      entity_id: folder.id,
      entity_type: "section"
    });
  }, [folder.id]);

  const onAddTaskWithRole = useDealRoleGuard(onAddTask, [RoleName.Admin, RoleName.Analyst]);
  const onCreateNewFolderWithRole = useDealRoleGuard(onCreateNewFolder, [RoleName.Admin, RoleName.Analyst]);
  const onAddFormWithRole = useDealRoleGuard(onAddForm, [RoleName.Admin, RoleName.Analyst]);
  const onAddDocumentWithRole = useDealRoleGuard(onAddDocument, [RoleName.Admin, RoleName.Analyst]);
  const onFileUploadWithRole = useDealRoleGuard(onFileUpload, [RoleName.Admin, RoleName.Analyst, RoleName.Guest]);
  const onDropFilesWithRole = useDealRoleGuard(onDropFiles, [RoleName.Admin, RoleName.Analyst, RoleName.Guest]);
  const onRenameFolderWithRole = useDealRoleGuard(onRenameFolder, [RoleName.Admin, RoleName.Analyst]);
  const onDeleteFolderWithRole = useDealRoleGuard(onDeleteFolder, [RoleName.Admin, RoleName.Analyst]);
  const onShareDialogWithRole = useDealRoleGuard(onShareDialog, [RoleName.Admin, RoleName.Analyst]);
  const onSetPrivateWithRole = useDealRoleGuard(onSetPrivate, [RoleName.Admin, RoleName.Analyst]);

  return {
    onCreateNewFolder: onCreateNewFolderWithRole,
    onAddForm: onAddFormWithRole,
    onAddDocument: onAddDocumentWithRole,
    onFileUpload: onFileUploadWithRole,
    onDropFiles: onDropFilesWithRole,
    onRenameFolder: onRenameFolderWithRole,
    onDeleteFolder: onDeleteFolderWithRole,
    onShareDialog: onShareDialogWithRole,
    onSetPrivate: onSetPrivateWithRole,
    checkOnSetPrivatePermission,
    onAddTask: onAddTaskWithRole
  };
};
